'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import Image from 'next/image';
import LinkNoPrefetch from '@/components/link-noprefetch';
import { encodeAssetUrl } from '@/app/utils/stringUtils';

const ITEMS_PER_PAGE = 12;

function getThumbSrc(thumbnail) {
    if (!thumbnail) return null;
    if (typeof thumbnail === 'object' && thumbnail.identifier) {
        return encodeAssetUrl(`/dA/${thumbnail.identifier}`);
    }
    if (typeof thumbnail === 'string') return encodeAssetUrl(thumbnail);
    return null;
}

function VideoCard({ video }) {
    const src = getThumbSrc(video.thumbnail);
    const title = video.title?.replace(/\.[^/.]+$/, '') || '';
    const [loaded, setLoaded] = useState(false);

    return (
        <LinkNoPrefetch href={`/videos/${video.urlTitle}`} className="group flex flex-col gap-3">
            <div className="relative aspect-video overflow-hidden rounded-xl bg-gray-900">
                {!loaded && <div className="absolute inset-0 animate-pulse bg-white/10" />}
                {src ? (
                    <Image
                        src={src}
                        alt={video.thumbnailAlt || title}
                        fill
                        onLoad={() => setLoaded(true)}
                        className={`object-cover transition-all duration-300 group-hover:scale-105 ${loaded ? 'opacity-100' : 'opacity-0'}`}
                        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    />
                ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-fuschia-700 to-fuschia-900" />
                )}
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                    focusable="false"
                    className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 opacity-0 transition-all duration-300 group-hover:opacity-100">
                    <path
                        fill="white"
                        d="m9.5 16.5l7-4.5l-7-4.5zM12 22q-2.075 0-3.9-.788t-3.175-2.137T2.788 15.9T2 12t.788-3.9t2.137-3.175T8.1 2.788T12 2t3.9.788t3.175 2.137T21.213 8.1T22 12t-.788 3.9t-2.137 3.175t-3.175 2.138T12 22"
                    />
                </svg>
                <div className="absolute inset-x-0 size-full bg-black/20 opacity-0 transition-all group-hover:opacity-100" />
            </div>
            <p className="line-clamp-2 text-base font-semibold leading-snug text-white/80 transition-colors group-hover:text-white">
                {title}
            </p>
        </LinkNoPrefetch>
    );
}

function SearchIcon() {
    return (
        <svg
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40"
            width="18"
            height="18"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true">
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
            />
        </svg>
    );
}

export default function VideosListClient({ initialVideos = [], topics = [] }) {
    const [inputValue, setInputValue] = useState('');
    const [search, setSearch] = useState('');
    const [activeTopics, setActiveTopics] = useState(new Set());
    const [videos, setVideos] = useState(initialVideos.slice(0, ITEMS_PER_PAGE));
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(initialVideos.length >= ITEMS_PER_PAGE);
    const [isLoading, setIsLoading] = useState(false);
    const isFiltered = !!search || activeTopics.size > 0;
    const initialRef = useRef(initialVideos);
    const abortRef = useRef(null);

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => setSearch(inputValue), 350);
        return () => clearTimeout(timer);
    }, [inputValue]);

    const doFetch = useCallback(async ({ s, t, p, append = false }) => {
        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        setIsLoading(true);
        try {
            const params = new URLSearchParams({
                limit: String(ITEMS_PER_PAGE),
                page: String(p)
            });
            if (s) params.set('search', s);
            t.forEach((topic) => params.append('topic', topic));

            const res = await fetch(`/api/videos?${params}`, { signal: controller.signal });
            if (!res.ok) throw new Error(`Request failed: ${res.status}`);
            const data = await res.json();
            const fetched = data.videos || [];

            setVideos((prev) => (append ? [...prev, ...fetched] : fetched));
            setHasMore(data.hasMore ?? false);
            setIsLoading(false);
        } catch (err) {
            if (err.name !== 'AbortError') {
                console.error('Failed to fetch videos', err);
                setIsLoading(false);
            }
        }
    }, []);

    // Re-fetch when search or active topics change
    useEffect(() => {
        setPage(1);
        if (search || activeTopics.size > 0) {
            doFetch({ s: search, t: [...activeTopics], p: 1 });
        } else {
            setVideos(initialRef.current.slice(0, ITEMS_PER_PAGE));
            setHasMore(initialRef.current.length >= ITEMS_PER_PAGE);
        }
    }, [search, activeTopics, doFetch]);

    const handleTopic = (topic) => {
        setActiveTopics((prev) => {
            const next = new Set(prev);
            next.has(topic) ? next.delete(topic) : next.add(topic);
            return next;
        });
    };

    const handleLoadMore = () => {
        const next = page + 1;
        setPage(next);
        doFetch({ s: search, t: [...activeTopics], p: next, append: true });
    };

    const clearFilters = () => {
        setInputValue('');
        setSearch('');
        setActiveTopics(new Set());
    };

    return (
        <section className="w-full px-5 py-12">
            <div className="container p-0">
                {/* Search + Topic filters */}
                <div className="mb-10 flex flex-col gap-5">
                    <div className="relative max-w-lg">
                        <SearchIcon />
                        <input
                            type="text"
                            placeholder="Search videos..."
                            aria-label="Search videos"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            className="w-full rounded-xl border border-white/20 bg-white/10 py-3 pl-12 pr-4 text-white outline-none transition placeholder:text-white/40 focus:border-white/40 focus:bg-white/15"
                        />
                        {inputValue && (
                            <button
                                onClick={() => {
                                    setInputValue('');
                                    setSearch('');
                                }}
                                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                                aria-label="Clear search">
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="currentColor"
                                    aria-hidden="true">
                                    <path
                                        d="M4 4l8 8M12 4l-8 8"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </button>
                        )}
                    </div>

                    {topics.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={clearFilters}
                                className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${
                                    activeTopics.size === 0 && !search
                                        ? 'bg-white text-gray-900'
                                        : 'border border-white/25 text-white/60 hover:border-white/50 hover:text-white'
                                }`}>
                                All
                            </button>
                            {topics.map((topic) => (
                                <button
                                    key={topic}
                                    onClick={() => handleTopic(topic)}
                                    className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${
                                        activeTopics.has(topic)
                                            ? 'bg-white text-gray-900'
                                            : 'border border-white/25 text-white/60 hover:border-white/50 hover:text-white'
                                    }`}>
                                    {topic}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Video grid */}
                {isLoading && !videos.length ? (
                    <div className="flex items-center justify-center py-24">
                        <div className="size-10 animate-spin rounded-full border-4 border-white/20 border-t-white" />
                    </div>
                ) : videos.length ? (
                    <>
                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                            {videos.map((video) => (
                                <VideoCard key={video.identifier} video={video} />
                            ))}
                        </div>
                        {(hasMore || isLoading) && (
                            <div className="mt-12 flex justify-center">
                                <button
                                    onClick={handleLoadMore}
                                    disabled={isLoading}
                                    className="rounded-xl border border-white/25 px-10 py-3 text-sm font-medium text-white transition hover:bg-white/10 disabled:opacity-40">
                                    {isLoading ? (
                                        <span className="flex items-center gap-2">
                                            <span className="inline-block size-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                                            Loading…
                                        </span>
                                    ) : (
                                        'Load more'
                                    )}
                                </button>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="flex flex-col items-center justify-center gap-3 py-24 text-white/50">
                        <p className="text-lg">No videos found</p>
                        {isFiltered && (
                            <button
                                onClick={clearFilters}
                                className="text-sm underline hover:text-white">
                                Clear filters
                            </button>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
}
